import { runTool, type ToolRunResult } from "idfweb-toolrunner";
import type { Vfs } from "idfweb-vfs";
import type { ExecutionPolicies, ExecutionPolicy, Preopen } from "idfweb-wasix";

/** Guest filesystem mount points and toolchain execution helpers. */

/** Toolchain binaries, headers, and CMake modules. */
export const GUEST_USR = "/usr";

/** Shell and toolchain binary alias directory. */
export const GUEST_BIN = "/bin";

/** The workspace folder: the parent of the opened tree. */
export const GUEST_PROJECT = "/project";

/**
 * Where a tree opened under `name` goes: a directory in `GUEST_PROJECT` named as
 * the tree's own directory is. ESP-IDF identifies a component by its directory,
 * so a tree that is itself a component, such as a component repository whose
 * example points at it with `override_path`, keeps its name.
 */
export function guestTree(name: string): string {
    const last = name
        .split("/")
        .filter((part) => part !== "")
        .pop();
    const safe = last === undefined || last === "." || last === ".." ? "project" : last;
    return `${GUEST_PROJECT}/${safe}`;
}

/** ESP-IDF framework directory. */
export const GUEST_IDF = "/idf";

/** Scratch directory for temporary compiler files. */
export const GUEST_TMP = "/tmp";

/**
 * CPython, which esp-ice (`python3`) runs a script in when it has no equivalent
 * of its own. The version is the CPYTHON_VERSION pin's major and minor.
 */
export const GUEST_CPYTHON = `${GUEST_USR}/bin/python3.14`;

/** Target CMake toolchain file for standalone projects. */
export const TOOLCHAIN_FILE = `${GUEST_USR}/share/idfweb/riscv32-esp-elf.cmake`;

/** Guest mount point directories in preopen order. */
export const GUEST_MOUNTS: readonly string[] = [GUEST_USR, GUEST_BIN, GUEST_IDF, GUEST_PROJECT, GUEST_TMP];

/** Memory allocation limits for tool execution. */
const FIRST_ATTEMPT = 512 * 1024 * 1024;
const SECOND_ATTEMPT = 4 * 1024 * 1024 * 1024;

const LINKERS = [
    "ld.lld",
    "riscv32-esp-elf-clang-ld",
    "xtensa-esp32-elf-clang-ld",
    "xtensa-esp32s2-elf-clang-ld",
    "xtensa-esp32s3-elf-clang-ld",
];

const XTENSA_CHIPS = ["esp32", "esp32s2", "esp32s3"];

/**
 * The versions of every GCC build-tools builds (the GCC_*_VERSION pins), which
 * give the directory its driver runs cc1 and collect2 from. An asset set holds
 * one of them, the one its ESP-IDF names.
 */
export const GCC_VERSIONS = ["16.1.0", "15.2.0"];

/** The GCC tools installed under each target prefix, beside the assembler. */
const GCC_TOOLS = ["gcc", "g++", "ld", "gcc-ar", "gcc-ranlib", "ar", "ranlib"];

const PER_EDGE_TOOLS = [
    ...LINKERS,
    "clang",
    "clang++",
    "llvm-ar",
    "llvm-ranlib",
    ...XTENSA_CHIPS.flatMap((chip) => ["clang-as", ...GCC_TOOLS].map((tool) => `xtensa-${chip}-elf-${tool}`)),
    ...["as", ...GCC_TOOLS].map((tool) => `riscv32-esp-elf-${tool}`),
];

/**
 * What GCC's drivers run, by guest path: each Xtensa chip's compiler from a
 * libexec directory of its own, the RISC-V compiler from GCC's usual one. `as`
 * and `ld` there are links to the tools in bin.
 */
const GCC_PROGRAMS = GCC_VERSIONS.flatMap((version) => [
    ...XTENSA_CHIPS.map((chip) => `${GUEST_USR}/libexec/xtensa-${chip}/gcc/xtensa-esp-elf/${version}`),
    `${GUEST_USR}/libexec/gcc/riscv32-esp-elf/${version}`,
]).flatMap((dir) => ["cc1", "cc1plus", "collect2", "as", "ld"].map((program) => `${dir}/${program}`));

/** Single-threaded link flags for LLD. */
const NO_LINKER_THREADS = ["--threads=1"];

/** Execution policies defining memory limits and arguments per guest tool path. */
export const EXECUTION_POLICIES: ExecutionPolicies = {
    default: { attempts: [FIRST_ATTEMPT] },
    ...(Object.fromEntries(
        PER_EDGE_TOOLS.flatMap((tool) => {
            const policy: ExecutionPolicy = {
                attempts: [FIRST_ATTEMPT, SECOND_ATTEMPT],
                ...(LINKERS.includes(tool) ? { args: NO_LINKER_THREADS } : {}),
            };
            return [
                [`${GUEST_USR}/bin/${tool}`, policy],
                [`${GUEST_BIN}/${tool}`, policy],
            ];
        }),
    ) as Record<string, ExecutionPolicy>),
    ...Object.fromEntries(
        GCC_PROGRAMS.map((path) => [path, { attempts: [FIRST_ATTEMPT, SECOND_ATTEMPT] } satisfies ExecutionPolicy]),
    ),
    // The host has no threads, so clangd serves requests on its main thread
    // (--sync) and indexes only the open files. --query-driver lets it ask the
    // compiler in compile_commands.json for its include paths and target. At
    // the default result limit of 100, completion over the ESP-IDF headers
    // finds nothing for prefixes such as "esp_" and "ESP".
    [`${GUEST_USR}/bin/clangd`]: {
        attempts: [SECOND_ATTEMPT],
        args: [
            "--sync",
            "--background-index=false",
            "--pch-storage=memory",
            "--limit-results=1000",
            `--query-driver=${GUEST_USR}/bin/*,${GUEST_BIN}/*`,
        ],
    },
};

export interface Toolchain {
    /** Guest virtual filesystem containing toolchain assets, project, and output. */
    vfs: Vfs;
    /** Preopened guest directories available to spawned tools. */
    preopens: Preopen[];
    trace?: ((line: string) => void) | undefined;
}

/** Construct a toolchain configuration over a Vfs instance. */
export function toolchain(vfs: Vfs, trace?: ((line: string) => void) | undefined): Toolchain {
    return { vfs, preopens: [...GUEST_MOUNTS], trace };
}

/** Construct standard environment variables for guest tools. */
export function toolchainEnv(): Record<string, string> {
    return {
        PATH: `${GUEST_USR}/bin:${GUEST_BIN}`,
        HOME: GUEST_PROJECT,
        TMPDIR: GUEST_TMP,
        IDF_PATH: GUEST_IDF,
        // Kconfig menu ids hold the file's path relative to srctree, else to the working
        // directory: fixed, the configure, ninja's reconfigure and kconfserver agree.
        srctree: "/",
        ICE_PYTHON: GUEST_CPYTHON,
        // CPython caches bytecode beside the sources, and /usr is the CLI's
        // asset directory on disk.
        PYTHONPYCACHEPREFIX: `${GUEST_TMP}/pycache`,
    };
}

/** Run a toolchain binary specified by argv[0]. */
export async function runInToolchain(
    tc: Toolchain,
    argv: string[],
    opts: {
        cwd?: string | undefined;
        /** Added to `toolchainEnv()`. */
        env?: Record<string, string> | undefined;
        onStdout?: ((bytes: Uint8Array) => void) | undefined;
        onStderr?: ((bytes: Uint8Array) => void) | undefined;
        signal?: AbortSignal | undefined;
    } = {},
): Promise<ToolRunResult> {
    const argv0 = argv[0];
    if (argv0 === undefined) throw new Error("empty argv");
    return runTool({
        vfs: tc.vfs,
        program: argv0,
        args: argv,
        env: { ...toolchainEnv(), ...opts.env },
        // Defaults to /usr to allow Clang to locate its relative resource directory.
        cwd: opts.cwd ?? GUEST_USR,
        preopens: tc.preopens,
        policies: EXECUTION_POLICIES,
        trace: tc.trace,
        onStdout: opts.onStdout,
        onStderr: opts.onStderr,
        signal: opts.signal,
    });
}
